<script setup lang="ts">
import type { ICurrency, IWallet } from "@/user-account/types/UserAccount.type"
import { fetchBillingsCurrencies, fetchWallets } from "@/app/api"
import BaseTextField from "@/app/components/ui/BaseTextField.vue"
import { useCurrencies } from "@/app/composables/useCurrencies"
import { t } from "@/app/composables/useI18n"
import { useDepositFormStore } from "@/app/stores/depositFormStore"
import { useUserService } from "@/user-account/composables/useUserService"
import { computed, onMounted, ref, watch } from "vue"
import ClipLoader from "vue-spinner/src/ClipLoader.vue"
import SelectItem from "../../SelectItem.vue"

const props = withDefaults(
  defineProps<{
    type?: string
  }>(),
  {
    type: "deposit",
  },
)

const DEPOSIT_AMOUNTS = [25, 50, 100, 200, 500, 1000]
const ALLOWED_CURRENCIES = ["ETH", "USDC", "USDTT", "USDTE", "SOL"]

const { fundWallets, activeWallet, user } = useUserService()
const fundWalletsWithAddresss = computed(() => {
  return fundWallets.value.filter((wallet) => {
    const hasAddress = wallet.address && wallet.type === "funds"

    const currencyName = wallet.currency?.ticker || ""
    const isAllowedCurrency = ALLOWED_CURRENCIES.includes(currencyName.toUpperCase())

    return hasAddress && isAllowedCurrency
  })
})
const { getIconByTicker } = useCurrencies()
const allowedCurrencies = ref<ICurrency[]>([])
const loading = ref(false)
const selectedCurrency = ref<ICurrency | Record<string, never>>({})
const isDropdownOpen = ref(false)
const userWallets = ref<IWallet[]>([])
const selectedWallet = ref<IWallet | null>(null)

const actionNames = {
  deposit: "deposit",
}

const depositFormStore = useDepositFormStore()

const selectedWalletData = computed(() => {
  if (!selectedCurrency.value?.id) {
    return fundWalletsWithAddresss.value[0] || null
  }

  return fundWalletsWithAddresss.value.find(
    wallet => wallet.currency.id === selectedCurrency.value.id,
  ) || fundWalletsWithAddresss.value[0] || null
})

watch(selectedWalletData, (newWalletData) => {
  if (newWalletData?.address) {
    depositFormStore.setFormData("cryptoAddress", newWalletData.address)
    depositFormStore.setFormData("currency", newWalletData.currency.ticker || "")
  }
}, { immediate: true })

async function loadAllowedCurrencies() {
  allowedCurrencies.value = []
  loading.value = true

  const params = {
    type: actionNames[props.type],
  }

  const { data } = await fetchBillingsCurrencies(params)

  allowedCurrencies.value = data.data || []
  loading.value = false
  selectedCurrency.value = activeWallet.value.currency

  if (selectedCurrency.value?.id) {
    depositFormStore.setFormData(
      "cryptoAddress",
      user.value.wallets.find(wallet => wallet.currency.id === selectedCurrency.value.id)?.address || "",
    )
    depositFormStore.setFormData("phone", user.value?.phone || "")
    depositFormStore.setFormData("email", user.value?.email || "")
  }
}

async function loadUserWallets() {
  try {
    loading.value = true

    const { data } = await fetchWallets()

    const wallets: IWallet[] = data.data.filter((w: IWallet) => !!w.address) || []
    userWallets.value = wallets

    if (selectedCurrency.value?.id) {
      const matchingWallet = wallets.find(
        wallet => wallet.currency.id === selectedCurrency.value.id && wallet.type === "funds",
      )

      if (matchingWallet) {
        selectedWallet.value = matchingWallet
      }
    }
  }
  catch (error) {
    console.error("Failed to load wallets:", error)
  }
  finally {
    loading.value = false
  }
}

function selectCurrency(currency: ICurrency) {
  selectedCurrency.value = currency

  const matchingWallet = userWallets.value.find(
    wallet => wallet.currency.id === currency.id && wallet.type === "funds",
  )

  if (matchingWallet) {
    selectedWallet.value = matchingWallet
  }
}

function handleToggle(value: boolean) {
  isDropdownOpen.value = value
}

onMounted(() => {
  if (activeWallet.value) {
    selectedCurrency.value = activeWallet.value.currency
    depositFormStore.setFormData(
      "cryptoAddress",
      user.value.wallets.find(wallet => wallet.currency.id === selectedCurrency.value.id)?.address || "",
    )
    depositFormStore.setFormData("phone", user.value?.phone || "")
    depositFormStore.setFormData("email", user.value?.email || "")
  }
  loadAllowedCurrencies()
  loadUserWallets()
})
</script>

<template>
  <div v-if="allowedCurrencies.length > 0">
    <div class="label">
      {{ t("finance.select_currency") }}
    </div>
    <div class="select-item">
      <SelectItem
        :value="selectedWalletData?.currency?.name || ''"
        :icon="selectedWalletData?.currency?.ticker || ''"
        :is-open="isDropdownOpen"
        @toggle="handleToggle"
      >
        <template #header>
          <div
            v-for="currency in fundWalletsWithAddresss"
            :key="currency?.currency?.id"
            @click="selectCurrency(currency?.currency)"
          >
            <div class="currency-balance">
              <img
                :src="getIconByTicker(currency?.currency?.ticker)"
                class="currency-icon"
                alt=""
              >
              {{ currency?.currency?.name }}
            </div>
          </div>
        </template>
      </SelectItem>
    </div>

    <div class="fields-container">
      <BaseTextField
        :model-value="depositFormStore.formData.cryptoAddress"
        :label="t('finance.deposit_address')"
        type="text"
        :readonly="true"
        icon-name="copy"
      />

      <div>
        <div class="label">
          {{ t("finance.deposit_amount") }}
        </div>
        <input v-model="depositFormStore.formData.depositAmount" class="deposit-amount" type="number">
      </div>
      <div class="deposit-amounts">
        <button
          v-for="amount in DEPOSIT_AMOUNTS" :key="amount"
          class="btn-g"
          :class="{ active: depositFormStore.formData.depositAmount === amount }"
          @click="depositFormStore.setFormData('depositAmount', amount)"
        >
          {{ amount }}
        </button>
      </div>
    </div>
  </div>

  <ClipLoader v-if="loading" :loading="true" color="white" size="20px" />

  <div v-if="allowedCurrencies.length === 0 && !loading">
    <span v-if="props.type === 'deposit'">{{ t("finance.deposit-not-allowed") }}</span>
    <span v-else>{{ t("finance.withdrawals_not_posible_message") }}</span>
  </div>
</template>

<style lang="scss" scoped>
.deposit-amounts {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 375px) {
    grid-template-columns: repeat(2, 1fr);
  }

  button {
    min-width: 80px;
    width: auto;
    margin: 0;

    @media (max-width: 480px) {
    height: 30px;
  }

    &.active {
      outline: 2px solid white;
    }

    &:hover {
      opacity: 0.8;
    }
  }
}

.fields-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.btn-g {
  color: var(--be-primary-btn-label);
  border: none;
  height: 42px;
  width: 100%;
  border-radius: 4px;
  background: var(--Linear, linear-gradient(90deg, #3886ec 0%, #544fe5 100%));
  margin: 12px 0px;
}

.currency-balance {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.currency-icon {
  width: 16px;
  height: 21px;
}

.withdraw-fee {
  font-size: 12px;
}

.label {
  margin-bottom: 7px;
}

.select-item {
  margin-bottom: 10px;
  height: 40px;
}

.deposit-amount {
  font-size: 14px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  border: none;
  height: 40px;
  outline: none;
  width: 100%;
  color: white;
  text-indent: 10px;
  opacity: 1;
}

.min-deposit {
  display: flex;
  justify-content: space-between;
  margin-top: 15px;
}

.qr-code {
  display: flex;
  padding: 16px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  gap: 0 16px;
}

.label {
  margin-bottom: 7px;
}

.deposit-amount {
  font-size: 14px;
  border-radius: 4px;
  background: var(--be-small-card-bg);
  border: none;
  height: 40px;
  outline: none;
  width: 100%;
  color: white;
  text-indent: 10px;
}
</style>
